import React, { useEffect, useMemo, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { DocumentIcon, GlobeAltIcon } from '@heroicons/react/24/outline';

/**
 * Markdown fuer Chat-Antworten — Port aus MessageContent.svelte, aber ohne
 * dangerouslySetInnerHTML: dieselbe Technik wie powerCONTACTs MarkdownContent.
 *
 *  • Wikilinks [[PID/datei|label]] werden VOR dem Rendern zu Markdown-Links
 *    mit dem Schema #um-wiki-N umgeschrieben; der `a`-Override faengt sie ab
 *    und rendert einen klickbaren Quellen-Chip.
 *  • Vega-Lite-Bloecke (```vega-lite / ```json mit vega-Schema) werden per
 *    Klammer-Abgleich extrahiert — auch wenn das Modell den Block nicht
 *    geschlossen hat (Streaming) — und als Platzhalter-Link #um-vega-N in
 *    eine Chart-Komponente geleitet, die vega-embed dynamisch laedt.
 *  • Externe Links bekommen einen Chip mit Host-Suffix.
 */

// [[PATIENT-ID/filename(.md)?|optional-label]] — ID-Formate siehe backend/app/library.py
const WIKILINK_RE =
  /\[\[((?:[A-Z]{2,4}-[A-Z0-9][A-Z0-9-]*|[A-Z0-9]{3,8}_[A-Za-zÀ-ÿ\-]+_[A-Za-zÀ-ÿ\-]+(?:_(?:\d{2}\.\d{2}\.\d{4}|00000000))?))\/([^\]|]+?)(?:\|([^\]]+))?\]\]/g;
const WIKI_HREF = /^#um-wiki-(\d+)$/;
const VEGA_HREF = /^#um-vega-(\d+|stream)$/;

export type WikiSource = { patient_id: string; filename: string };
type Wiki = WikiSource & { label: string };
type VegaBlock = { start: number; end: number; spec?: any; streaming?: boolean };

function findVegaBlocks(src: string): VegaBlock[] {
  const out: VegaBlock[] = [];
  const opener = /```(vega-lite|json)\s*\n/g;
  let m: RegExpExecArray | null;
  while ((m = opener.exec(src))) {
    const lang = m[1], start = m.index, afterOpen = start + m[0].length;
    let depth = 0, started = false, inStr = false, esc = false, endOfJson = -1;
    for (let i = afterOpen; i < src.length; i++) {
      const c = src[i];
      if (esc) { esc = false; continue; }
      if (c === '\\') { esc = true; continue; }
      if (c === '"') { inStr = !inStr; continue; }
      if (inStr) continue;
      if (c === '{') { depth++; started = true; }
      else if (c === '}') { depth--; if (depth === 0 && started) { endOfJson = i + 1; break; } }
    }
    if (endOfJson < 0) {
      const body = src.slice(afterOpen);
      const looksLikeVega = lang === 'vega-lite' || (body.includes('"$schema"') && body.includes('vega-lite'))
        || /^\s*\{\s*"(\$schema|title|data|mark|encoding|layer|width|height)"/.test(body);
      if (looksLikeVega) out.push({ start, end: src.length, streaming: true });
      break;
    }
    let spec: any = null;
    try { spec = JSON.parse(src.slice(afterOpen, endOfJson)); } catch { /* kein JSON */ }
    let end = endOfJson;
    const tail = src.slice(endOfJson).match(/^\s*```/);
    if (tail) end = endOfJson + tail[0].length;
    if (typeof spec?.$schema === 'string' && spec.$schema.includes('vega-lite')) { out.push({ start, end, spec }); opener.lastIndex = end; }
  }
  return out;
}

/** Vega-Spec absichern (Audit 07.09.): keine externen Datenquellen (data.url laedt mit Cookies),
 *  keine Bild-Marks (Exfiltration ueber Bild-URLs). Diagramme brauchen nur eingebettete Werte. */
function specBereinigen(spec: any): any {
  const walk = (o: any): any => {
    if (Array.isArray(o)) return o.map(walk);
    if (o && typeof o === 'object') {
      const out: any = {};
      for (const [k, v] of Object.entries(o)) {
        if (k === 'url' || k === 'format' && typeof v === 'object' && v && 'url' in (v as any)) continue;
        if (k === 'mark' && (v === 'image' || (v && typeof v === 'object' && (v as any).type === 'image'))) throw new Error('Bild-Marks sind nicht erlaubt');
        out[k] = walk(v);
      }
      return out;
    }
    return o;
  };
  return walk(spec);
}

function VegaChart({ spec }: { spec: any }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let cancelled = false;
    const el = ref.current;
    if (!el) return;
    el.innerHTML = '';
    (async () => {
      try {
        const embed = (await import('vega-embed')).default;
        if (cancelled || !ref.current) return;
        await embed(ref.current, specBereinigen(spec), { loader: { baseURL: '', mode: 'vega-lite' } as any, actions: { export: true, source: false, compiled: false, editor: false }, renderer: 'svg', tooltip: { theme: 'light' } });
      } catch (err: any) {
        if (ref.current) ref.current.innerHTML = `<div class="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-[12.5px] text-rose-700">Diagramm konnte nicht gezeichnet werden: ${String(err?.message ?? err).replace(/[<>&]/g, '')}</div>`;
      }
    })();
    return () => { cancelled = true; };
  }, [spec]);
  return <div ref={ref} data-kopie="diagramm" className="my-3 overflow-x-auto rounded-md border border-gray-200 bg-white p-3" role="img" aria-label="Diagramm" />;
}

export default function ChatMarkdown({ text, onSourceClick, compact = true }:
  { text: string; onSourceClick?: (s: WikiSource) => void; compact?: boolean }) {
  const { markdown, wikis, specs } = useMemo(() => {
    // 1. Vega-Bloecke raus
    const blocks = findVegaBlocks(text || '');
    const specs: any[] = [];
    let noVega = text || '';
    if (blocks.length) {
      const parts: string[] = []; let cursor = 0;
      for (const b of blocks) {
        parts.push(noVega.slice(cursor, b.start));
        if (b.streaming) parts.push('\n\n[Diagramm wird erstellt …](#um-vega-stream)\n\n');
        else if (b.spec) { parts.push(`\n\n[Diagramm](#um-vega-${specs.length})\n\n`); specs.push(b.spec); }
        cursor = b.end;
      }
      parts.push(noVega.slice(cursor));
      noVega = parts.join('');
    }
    // 2. Wikilinks → Markdown-Links
    const wikis: Wiki[] = [];
    let md = noVega.replace(WIKILINK_RE, (_m, pid: string, fn: string, label?: string) => {
      const filename = fn.trim().endsWith('.md') ? fn.trim() : fn.trim() + '.md';
      const base = fn.trim().replace(/\.(md|pdf)$/, '');
      const short = base.length > 30 ? base.slice(0, 28) + '…' : base;
      const idx = wikis.length;
      wikis.push({ patient_id: pid, filename, label: label?.trim() || short });
      return `[${(label?.trim() || short).replace(/[[\]]/g, '')}](#um-wiki-${idx})`;
    });
    // 3. Quellen-Links in Backticks aufloesen, damit sie klickbar bleiben
    md = md.replace(/`(\[[^\]`]+\]\([^)`]+\))`/g, '$1').replace(/`(\[[^\]`]+\])`\s*`?\((https?:\/\/[^)\s`]+)\)`?/g, '$1($2)');
    return { markdown: md, wikis, specs };
  }, [text]);

  const components = {
    a({ href, children, ...props }: any): JSX.Element {
      const w = typeof href === 'string' ? href.match(WIKI_HREF) : null;
      if (w) {
        const wiki = wikis[Number(w[1])];
        if (!wiki) return <span className="text-ps-dark-400">{children}</span>;
        return (
          <button type="button" data-kopie="weg" onClick={() => onSourceClick?.(wiki)} title={`Patientenakte: ${wiki.patient_id}/${wiki.filename}`}
            className="mx-0.5 inline-flex items-baseline gap-1 rounded border border-primary-300 bg-primary-50 px-1.5 py-px align-baseline font-mono text-[11px] font-medium leading-relaxed text-primary-600 hover:border-primary-500 hover:bg-primary-500 hover:text-white">
            <DocumentIcon className="h-2.5 w-2.5 translate-y-px" /><span>{wiki.label}</span>
          </button>
        );
      }
      const v = typeof href === 'string' ? href.match(VEGA_HREF) : null;
      if (v) {
        if (v[1] === 'stream') return <span data-kopie="weg" className="my-3 flex items-center justify-center gap-2 rounded-md border border-dashed border-primary-300 bg-gray-50 px-3 py-6 text-xs italic text-ps-dark-400"><span className="h-3 w-3 animate-spin rounded-full border-[1.5px] border-primary-200 border-t-primary-500" /> Diagramm wird erstellt …</span>;
        const spec = specs[Number(v[1])];
        return spec ? <VegaChart spec={spec} /> : <span />;
      }
      if (typeof href === 'string' && /^https?:\/\//i.test(href)) {
        let host = ''; try { host = new URL(href).hostname.replace(/^www\./, ''); } catch { /* egal */ }
        return (
          <a href={href} target="_blank" rel="noopener noreferrer" title={href}
            className="mx-0.5 inline-flex items-baseline gap-1 rounded border border-gray-300 bg-gray-50 px-1.5 py-px align-baseline text-[11.5px] font-medium leading-relaxed text-ps-dark-900 no-underline hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-800">
            <GlobeAltIcon className="h-2.5 w-2.5 translate-y-px" /><span>{children}</span><span data-kopie="weg" className="font-mono text-[10px] text-ps-dark-400">· {host}</span>
          </a>
        );
      }
      return <a href={href} {...props} className="text-primary-500 underline decoration-primary-300 underline-offset-2 hover:decoration-primary-500">{children}</a>;
    },
    // Vega-Platzhalter stehen als eigener Absatz — der <p> darf keinen <div> enthalten.
    p({ children, ...props }: any): JSX.Element {
      const kids = React.Children.toArray(children);
      const nurVega = kids.length === 1 && React.isValidElement(kids[0]) && typeof (kids[0] as any).props?.href === 'string' && VEGA_HREF.test((kids[0] as any).props.href);
      return nurVega ? <>{children}</> : <p {...props}>{children}</p>;
    },
  };

  const spacing = compact
    ? ' leading-snug prose-p:my-1 prose-headings:mt-2 prose-headings:mb-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 prose-hr:my-2 first:prose-headings:mt-0'
    : '';
  return (
    <div className={'prose prose-sm max-w-none text-ps-dark-700 prose-headings:text-ps-dark-900 prose-strong:text-ps-dark-900 prose-code:rounded prose-code:border prose-code:border-gray-200 prose-code:bg-gray-50 prose-code:px-1 prose-code:text-ps-dark-700 prose-pre:bg-gray-50 prose-pre:text-ps-dark-700 prose-th:bg-gray-50 prose-table:text-[13px]' + spacing}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components as any}>{markdown}</ReactMarkdown>
    </div>
  );
}
